import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, State, Watch } from '@stencil/core';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import store from '@/store';
import { renderHiddenInput } from '@/utils';

export interface FdsToggleSwitchChangeEventDetail<T = any> {
  value: T;
  checked: boolean;
}

@Component({
  tag: 'fds-toggle-switch',
  styleUrl: 'styles/toggle-switch.scss',
  shadow: true,
})
export class ToggleSwitch {
  private toggleSwitchId = uuidv4();

  @Element() el!: HTMLFdsToggleSwitchElement;

  @State() isChecked = false;

  /**
   * Add a description text to the component
   */
  @Prop() description: string;

  /**
   * The name of the control, which is submitted with the form data.
   */
  @Prop() name = 'toggle-switch';

  /**
   * If true, will render a frame/border around the component
   */
  @Prop() framed = false;

  /**
   * If true, it will take up as much space as it can
   */
  @Prop() fullWidth = false;

  /**
   * If true, the component will be disabled and users cannot interact with it
   */
  @Prop() disabled = false;

  /**
   * If true, then the toggle will not toggle automatically, developers will have to control the logic.
   */
  @Prop() controlCheck = false;

  /**
   * Wether or not the toggle switch is checked by default
   */
  @Prop() defaultChecked = false;

  /**
   * Wether or not the toggle switch is checked (if contolCheck is on, you can control this)
   */
  @Prop() checked = false;

  @Watch('defaultChecked')
  @Watch('checked')
  checkedChanged(value: boolean) {
    this.isChecked = value;
  }
  /**
   * The value of the toggle does not mean if it's checked or not, use the `checked`
   * property for that.
   *
   * The value of a toggle is analogous to the value of a `<input type="checkbox">`,
   * it's only used when the toggle participates in a native `<form>`.
   */
  @Prop() value?: string | null = 'on';

  /**
   * Emitted when the toggle switch is toggled
   */
  // eslint-disable-next-line @stencil-community/decorators-style
  @Event()
  fdsToggleSwitchChecked: EventEmitter<FdsToggleSwitchChangeEventDetail>;

  componentWillLoad() {
    this.isChecked = this.defaultChecked;
  }

  @Listen('keydown')
  handleKeyDown(ev: KeyboardEvent) {
    const { disabled } = this;

    if ((ev.key === 'Enter' || ev.key === ' ') && !disabled) {
      ev.preventDefault();
      this.toggleChecked();
    }
  }

  private getValue() {
    return this.value || '';
  }

  private toggleChecked() {
    const { isChecked, value, fdsToggleSwitchChecked, controlCheck } = this;

    if (controlCheck) {
      fdsToggleSwitchChecked.emit({ checked: isChecked, value });
    } else {
      const newIsChecked = !isChecked;
      this.isChecked = !isChecked;

      fdsToggleSwitchChecked.emit({ checked: newIsChecked, value });
    }
  }

  private handleClick() {
    if (!this.disabled) {
      this.toggleChecked();
    }
  }

  private labelText() {
    return document.querySelector('fds-toggle-switch')?.textContent;
  }

  render() {
    const { disabled, framed, fullWidth, isChecked, toggleSwitchId, description, name, el } = this;

    const value = this.getValue();

    renderHiddenInput(true, el, name, isChecked ? value : '', disabled);

    return (
      <Host
        class={clsx(store.mode ?? '', {
          ['fds-toggle-switch--disabled']: disabled,
          ['fds-toggle-switch--framed']: framed,
          ['fds-toggle-switch--full-width']: fullWidth,
          ['fds-toggle-switch--checked']: isChecked,
        })}
        part="switch"
        onClick={() => this.handleClick()}
      >
        <label class="fds-toggle-switch__toggle-wrapper">
          <input
            type="checkbox"
            role="switch"
            // eslint-disable-next-line jsx-a11y/aria-proptypes
            aria-checked={`${isChecked}`}
            defaultChecked={isChecked}
            checked={isChecked}
            disabled={disabled}
            id={toggleSwitchId}
            name={name}
            onClick={(event) => event.stopPropagation()}
          />
          <div class="fds-toggle-switch__text-wrapper">
            <span class="fds-toggle-switch__label" data-label={this.labelText()}>
              <slot />
            </span>
            <span data-description={description} class="fds-toggle-switch__description">
              {description}
            </span>
          </div>
          <div class="fds-toggle-switch__switch" part="toggle">
            {isChecked && <fds-icon class="mode-switchIcon--checked" name="wb_sunny_filled" size={16} />}
            <span class="fds-toggle-switch__switch-knob">
              {isChecked && <fds-icon class="toggle-switchIcon" name="check" size={16} />}
            </span>
            {!isChecked && <fds-icon class="mode-switchIcon" name="moon_filled" size={16} />}
          </div>
        </label>
      </Host>
    );
  }
}
